// LA VIE DES BULLES DE BUBBLES, sans GPU : dérive lente dans un courant d'air,
// légère montée, collisions élastiques entre bulles (masse ∝ r³) et contre les
// bords d'une boîte de scène, naissance depuis le bas (la bulle grossit au bout
// d'un anneau invisible puis se détache), ÉCLATEMENT (le film se déchire depuis
// un point en POP_S = 80 ms) et sa couronne de gouttelettes, balistiques
// (p = p0 + v t + g t²/2 : le shader les calcule, seule leur naissance est écrite).
//
// Repère monde : y vers le haut, la caméra du côté des z positifs. La boîte de
// scène BOX est ce que la caméra voit, marge comprise (R6).

export const BOX = { x: 4.2, yLo: -2.3, yHi: 2.5, zLo: -3.0, zHi: 1.2 };
export const POP_S = 0.08;               // la déchirure s'ouvre en 80 ms
export const BIRTH_S = 1.4;              // la formation d'une bulle au bas de l'écran
export const GRAVITY = -4.5;             // (m/s², adouci : des gouttes de savon, vues de loin)
export const DROPS_PER_POP = 36;
export const DROP_LIFE = 1.6;
export const MAX_DROPS = 1024;
export const MAX_BUBBLES = 96;

// un générateur pseudo-aléatoire déterministe (les tests et la sonde rejouent la même scène)
export function rng(seed) { let s = (seed >>> 0) || 1; return () => (s = (s * 1664525 + 1013904223) >>> 0) / 4294967296; }

// la taille d'une bulle : très variée, beaucoup de petites, quelques géantes (loi de puissance)
export function drawRadius(r) { return 0.09 + 0.85 * Math.pow(r, 3.2); }

export class BubbleSim {
  constructor(count, seed) {
    this.rnd = rng(seed || 7);
    this.target = count;
    this.b = [];
    this.drops = new Float32Array(MAX_DROPS * 8);   // x y z t0 | vx vy vz size
    this.dropHead = 0; this.dropsDirty = true;
    this.time = 0;
    this.stats = { pops: 0, births: 0, collisions: 0 };
    for (let i = 0; i < count; i++) { let b = null; for (let k = 0; k < 40; k++) { b = this._make(true); if (!this._overlaps(b)) break; } this.b.push(b); }
  }
  _make(anywhere) {
    const R = this.rnd, r = drawRadius(R());
    const b = { r, rr: r, x: (R() * 2 - 1) * (BOX.x - r), y: anywhere ? BOX.yLo + r + R() * (BOX.yHi - BOX.yLo - 2 * r) : BOX.yLo - r * 0.2, z: BOX.zLo + r + R() * (BOX.zHi - BOX.zLo - 2 * r),
      vx: (R() - 0.5) * 0.12, vy: 0.02 + R() * 0.05, vz: (R() - 0.5) * 0.06, seed: R() * 100, born: anywhere ? -99 : this.time, pop: -1, popDir: [0, 1, 0], alive: true };
    return b;
  }
  _overlaps(b) { for (const c of this.b) if (Math.hypot(c.x - b.x, c.y - b.y, c.z - b.z) < c.r + b.r) return true; return false; }
  // une naissance : au bas de la boîte, la bulle grossit pendant BIRTH_S puis se détache et monte
  birth(x) {
    if (this.b.length >= MAX_BUBBLES) return null;
    const b = this._make(false);
    // un emplacement libre au bas (à la taille finale) : sinon la naissance attend
    let ok = false;
    for (let k = 0; k < 12 && !ok; k++) {
      const cx = k === 0 && x != null ? x : (this.rnd() * 2 - 1) * BOX.x * 0.85;
      b.x = Math.max(-BOX.x + b.r, Math.min(BOX.x - b.r, cx)); b.y = BOX.yLo + b.r;
      ok = !this._overlaps(b);
    }
    if (!ok) return null;
    b.y = BOX.yLo - b.r * 0.2;
    b.vy = 0; b.vx *= 0.3;
    this.b.push(b); this.stats.births++;
    return b;
  }
  // l'éclatement de la bulle i depuis le point de direction d (unitaire, repère de la bulle)
  pop(i, d) {
    const b = this.b[i];
    if (!b || b.pop >= 0 || this.time - b.born < BIRTH_S) return false;
    const R = this.rnd;
    let dir = d;
    if (!dir) { const th = R() * Math.PI * 2, u = R() * 2 - 1, s = Math.sqrt(1 - u * u); dir = [Math.cos(th) * s, u, Math.sin(th) * s]; }
    b.pop = this.time; b.popDir = dir; this.stats.pops++;
    // la couronne : le film qui se rétracte projette ses gouttes depuis le bord de la déchirure,
    // tout autour, vers l'extérieur et dans le sens de l'ouverture (vers -d) ; elles naissent pendant les 80 ms
    const t1 = [0, 0, 0], t2 = [0, 0, 0];
    const ax = Math.abs(dir[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
    t1[0] = dir[1] * ax[2] - dir[2] * ax[1]; t1[1] = dir[2] * ax[0] - dir[0] * ax[2]; t1[2] = dir[0] * ax[1] - dir[1] * ax[0];
    const l1 = Math.hypot(t1[0], t1[1], t1[2]); t1[0] /= l1; t1[1] /= l1; t1[2] /= l1;
    t2[0] = dir[1] * t1[2] - dir[2] * t1[1]; t2[1] = dir[2] * t1[0] - dir[0] * t1[2]; t2[2] = dir[0] * t1[1] - dir[1] * t1[0];
    const n = Math.round(DROPS_PER_POP * Math.min(1.6, 0.5 + b.r * 1.5));
    for (let k = 0; k < n; k++) {
      const a = R() * Math.PI * 2, open = R();                  // où le bord de la déchirure en est
      const ang = open * Math.PI;                                 // angle depuis le point de rupture
      const c = Math.cos(ang), s = Math.sin(ang);
      const nrm = [dir[0] * c + (t1[0] * Math.cos(a) + t2[0] * Math.sin(a)) * s, dir[1] * c + (t1[1] * Math.cos(a) + t2[1] * Math.sin(a)) * s, dir[2] * c + (t1[2] * Math.cos(a) + t2[2] * Math.sin(a)) * s];
      const sp = 0.35 + R() * 0.7;
      const o = this.dropHead * 8;
      this.drops[o] = b.x + nrm[0] * b.r; this.drops[o + 1] = b.y + nrm[1] * b.r; this.drops[o + 2] = b.z + nrm[2] * b.r; this.drops[o + 3] = this.time + open * POP_S;
      this.drops[o + 4] = b.vx + nrm[0] * sp; this.drops[o + 5] = b.vy + nrm[1] * sp; this.drops[o + 6] = b.vz + nrm[2] * sp; this.drops[o + 7] = 0.6 + R() * 0.8;
      this.dropHead = (this.dropHead + 1) % MAX_DROPS;
    }
    this.dropsDirty = true;
    return true;
  }
  // la bulle à faire éclater : grande, visible (devant), mûre
  pickPop(rnd) {
    let best = -1, bs = -1;
    for (let i = 0; i < this.b.length; i++) {
      const b = this.b[i]; if (b.pop >= 0 || this.time - b.born < BIRTH_S) continue;
      const s = (0.4 + b.r) * (0.6 + 0.4 * (b.z - BOX.zLo) / (BOX.zHi - BOX.zLo)) * (0.5 + rnd());
      if (s > bs) { bs = s; best = i; }
    }
    return best;
  }
  step(dt, wind) {
    this.time += dt;
    const W = wind || 0, t = this.time;
    // le courant d'air : un champ lent et sans divergence (rotationnel d'un potentiel), qui dérive
    for (const b of this.b) {
      if (b.pop >= 0) continue;
      const age = t - b.born;
      if (age < BIRTH_S) { b.rr = b.r * (0.15 + 0.85 * Math.min(1, age / BIRTH_S)); b.y = BOX.yLo + b.rr * 0.9; continue; }
      b.rr = b.r;
      const px = b.x * 0.45 + t * 0.05, py = b.y * 0.45 - t * 0.03;
      const fx = Math.cos(py * 2.1 + b.seed * 0.1) * 0.05, fy = -Math.sin(px * 1.7) * 0.04;
      b.vx += (fx * (1 + W) - b.vx * 0.25) * dt;
      b.vy += (fy + 0.035 / (0.4 + b.r) - b.vy * 0.25) * dt;     // la montée : plus lente pour les grosses
      b.vz += (Math.sin(t * 0.21 + b.seed) * 0.02 - b.vz * 0.25) * dt;
      b.x += b.vx * dt; b.y += b.vy * dt; b.z += b.vz * dt;
    }
    // les collisions élastiques (bulle contre bulle), sans interpénétration
    // (trois passes : une bulle coincée entre plusieurs voisines finit dégagée ; une bulle
    // qui naît est fixée à son anneau, masse infinie, et repousse les autres)
    const n = this.b.length;
    for (let pass = 0; pass < 3; pass++) for (let i = 0; i < n; i++) {
      const a = this.b[i]; if (a.pop >= 0) continue;
      for (let j = i + 1; j < n; j++) {
        const c = this.b[j]; if (c.pop >= 0) continue;
        const fa = t - a.born < BIRTH_S, fc = t - c.born < BIRTH_S;
        if (fa && fc) continue;
        const dx = c.x - a.x, dy = c.y - a.y, dz = c.z - a.z, d = Math.hypot(dx, dy, dz), m = a.rr + c.rr;
        if (d >= m || d < 1e-6) continue;
        const nx = dx / d, ny = dy / d, nz = dz / d;
        const ma = fa ? 1e9 : a.r * a.r * a.r, mc = fc ? 1e9 : c.r * c.r * c.r;
        // séparation (au prorata des masses), puis échange de quantité de mouvement le long de n
        const pen = m - d, ka = mc / (ma + mc), kc = ma / (ma + mc);
        a.x -= nx * pen * ka; a.y -= ny * pen * ka; a.z -= nz * pen * ka;
        c.x += nx * pen * kc; c.y += ny * pen * kc; c.z += nz * pen * kc;
        const vrel = (c.vx - a.vx) * nx + (c.vy - a.vy) * ny + (c.vz - a.vz) * nz;
        if (vrel < 0) {
          const jimp = -2 * vrel / (1 / ma + 1 / mc) * 0.92;     // élastique, un peu amorti
          a.vx -= jimp / ma * nx; a.vy -= jimp / ma * ny; a.vz -= jimp / ma * nz;
          c.vx += jimp / mc * nx; c.vy += jimp / mc * ny; c.vz += jimp / mc * nz;
          if (pass === 0) this.stats.collisions++;
        }
      }
    }
    // les bords : rebond doux (côtés, fond, devant) ; en haut, la bulle sort et renaît en bas
    for (let i = 0; i < this.b.length; i++) {
      const b = this.b[i]; if (b.pop >= 0 || t - b.born < BIRTH_S) continue;
      if (b.x < -BOX.x + b.r) { b.x = -BOX.x + b.r; b.vx = Math.abs(b.vx) * 0.8; }
      if (b.x > BOX.x - b.r) { b.x = BOX.x - b.r; b.vx = -Math.abs(b.vx) * 0.8; }
      if (b.z < BOX.zLo + b.r) { b.z = BOX.zLo + b.r; b.vz = Math.abs(b.vz) * 0.8; }
      if (b.z > BOX.zHi - b.r) { b.z = BOX.zHi - b.r; b.vz = -Math.abs(b.vz) * 0.8; }
      if (b.y < BOX.yLo + b.r) { b.y = BOX.yLo + b.r; b.vy = Math.abs(b.vy) * 0.8; }
      if (b.y > BOX.yHi + b.r * 2.5) { b.alive = false; }
    }
    // les bulles éclatées disparaissent après la déchirure ; la population revient vers sa cible par des naissances
    this.b = this.b.filter((b) => b.alive && !(b.pop >= 0 && t - b.pop > POP_S));
  }
  // les naissances qui ramènent la population à sa cible (appelées par la mesure ou à défaut)
  refill(maxN) {
    let k = 0;
    let tries = 0;
    while (this.b.length < this.target && k < (maxN || 99) && tries++ < 3 * (maxN || 99)) { if (this.birth((this.rnd() * 2 - 1) * BOX.x * 0.85)) k++; }
    return k;
  }
  // la position d'une goutte au temps t (comme le shader) ; null si pas née ou morte
  dropAt(k, t) {
    const o = k * 8, t0 = this.drops[o + 3]; const u = t - t0;
    if (!(t0 > 0) || u < 0 || u > DROP_LIFE) return null;
    return [this.drops[o] + this.drops[o + 4] * u, this.drops[o + 1] + this.drops[o + 5] * u + 0.5 * GRAVITY * u * u, this.drops[o + 2] + this.drops[o + 6] * u];
  }
}
